/* FOK shop catalogue — our own adaptation of Concept's collection.js.

   Concept's version is 734 lines built on Motion.js, theme.pubsub,
   theme.config/utils and its own DOM ids. None of that exists on a FOK page,
   so this reimplements only the mechanics that matter, against our markup:

     - a filter change re-renders the section over fetch instead of reloading
     - the URL always reflects the current filter state (shareable, back-safe)
     - an in-flight request is aborted when the user clicks again
     - sort and per-page are ordinary controls that submit the same form
     - "show more" reveals the options past the group's threshold

   PROGRESSIVE ENHANCEMENT. The sidebar is a real
   `<form method="GET" action="{{ request.path }}">`, so Shopify filters
   server-side and everything below is optional: with this file absent, or
   before it loads, a change simply submits the form and the page reloads with
   the right products. Nothing here is load-bearing for correctness.

   The `section_id` parameter is the whole trick: Shopify then returns ONLY
   this section's markup, which we parse and swap into our own containers. */
(function () {
  'use strict';

  function init(root) {
    if (root.__fokShopReady) return;
    root.__fokShopReady = true;

    var sectionId = root.getAttribute('data-section-id');
    var form = root.querySelector('[data-fok-shop-form]');
    var controller = null;

    /* /products and /collections resolve to list-collections.json, where
       Shopify ignores filter.* parameters completely — there is no
       `collection` object for them to act on, so an applied filter does not
       even register as active. The section still renders the whole catalogue
       there, but any filter, sort or paging interaction has to continue on a
       route where Shopify does apply it. `data-filter-route` carries that
       route, and interactions navigate to it with the chosen params. */
    var filterRoute = root.getAttribute('data-filter-route') || '';

    if (!form) return;

    /* ---- URL building ------------------------------------------------- */
    function buildUrl(extra) {
      var params = new URLSearchParams(new FormData(form));
      var url = new URL(form.action, window.location.origin);
      url.search = '';
      params.forEach(function (v, k) { url.searchParams.append(k, v); });

      /* Controls that live outside the form. */
      var sort = root.querySelector('[data-fok-shop-sort]');
      if (sort && sort.value) url.searchParams.set('sort_by', sort.value);
      /* per_page is only wired when the (off by default) Show control is
         enabled; Shopify cannot act on it, so it is carried but inert. */
      var per = root.querySelector('[data-fok-shop-perpage]');
      if (per && per.value) url.searchParams.set('per_page', per.value);

      if (extra) {
        Object.keys(extra).forEach(function (k) { url.searchParams.set(k, extra[k]); });
      }

      /* An empty field must not leave a dangling param. */
      var drop = [];
      url.searchParams.forEach(function (v, k) { if (v === '') drop.push(k); });
      drop.forEach(function (k) { url.searchParams.delete(k); });

      /* Any filter change invalidates the current page. */
      url.searchParams.delete('page');

      /* A search is part of the current view: filtering or sorting while a
         term is active must keep narrowing THOSE results, so the request has
         to go to /search carrying the term rather than back to the plain
         collection. Without this, changing sort mid-search silently threw
         the query away. */
      var activeTerm = '';
      var si = root.querySelector('[data-fok-shop-search] input[type="search"]');
      if (si && si.value.trim() !== '') activeTerm = si.value.trim();
      if (activeTerm) {
        url = new URL('/search', window.location.origin);
        params.forEach(function (v, k) { if (v !== '') url.searchParams.append(k, v); });
        var s2 = root.querySelector('[data-fok-shop-sort]');
        if (s2 && s2.value) url.searchParams.set('sort_by', s2.value);
        url.searchParams.set('q', activeTerm);
        url.searchParams.set('type', 'product');
      }

      /* form.action is the bare collection path, so a ?view=<template>
         preview parameter would be dropped on the very first filter change
         and the page would fall back to the default collection template.
         Shopify exposes no request.params.view to Liquid, so it is carried
         across here from the live URL. */
      var view = new URLSearchParams(window.location.search).get('view');
      if (view) url.searchParams.set('view', view);

      return url;
    }

    /* ---- render ------------------------------------------------------- */
    var REGIONS = [
      '[data-fok-shop-items]',
      '[data-fok-shop-count]',
      '.fok-shoppager',
      '.fok-shopempty'
    ];

    function swap(doc) {
      /* The sidebar is replaced wholesale so counts, disabled states and
         active checkmarks all refresh from the server's own numbers. */
      var newForm = doc.querySelector('[data-fok-shop-form]');
      if (newForm && form) {
        /* The mobile filter accordion lives inside the form, so replacing the
           form's markup would reset it to closed on every filter click —
           shutting the drawer under a user who is still filtering. Carry the
           open state across the swap. */
        var oldFilts = form.querySelector('[data-fok-shop-filts]');
        var wasOpen = oldFilts ? oldFilts.hasAttribute('open') : null;

        form.innerHTML = newForm.innerHTML;

        if (wasOpen !== null) {
          var nextFilts = form.querySelector('[data-fok-shop-filts]');
          if (nextFilts) {
            if (wasOpen) nextFilts.setAttribute('open', '');
            else nextFilts.removeAttribute('open');
            /* above the mobile tier the breakpoint rule wins over the
               remembered state — the filters are always shown there */
            if (!window.matchMedia('(max-width: 767px)').matches) {
              nextFilts.removeAttribute('data-fok-mobile');
              nextFilts.setAttribute('open', '');
            }
          }
        }
      }
      REGIONS.forEach(function (sel) {
        var next = doc.querySelector(sel);
        var current = root.querySelector(sel);
        if (next && current) {
          current.innerHTML = next.innerHTML;
        } else if (next && !current) {
          /* e.g. the empty state appearing where a grid used to be */
          var grid = root.querySelector('.fok-shopgrid');
          if (grid) grid.appendChild(next.cloneNode(true));
        } else if (!next && current) {
          current.remove();
        }
      });
      bindSidebar();
    }

    function render(url, displayUrl) {
      /* On a route that cannot filter, hand off to the one that can. */
      if (filterRoute) {
        var handoff = new URL(url.toString());
        handoff.pathname = filterRoute;
        handoff.searchParams.delete('view');
        window.location.assign(handoff.toString());
        return;
      }
      var fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set('section_id', sectionId);

      if (controller) controller.abort();
      controller = new AbortController();
      root.setAttribute('data-loading', '1');

      fetch(fetchUrl.toString(), { signal: controller.signal })
        .then(function (r) { return r.text(); })
        .then(function (text) {
          swap(new DOMParser().parseFromString(text, 'text/html'));
          root.removeAttribute('data-loading');
          /* The address bar shows where the shopper thinks they are — the
             catalogue — not /search, which is only where the markup was
             fetched from. `displayUrl` overrides it for inline search. */
          var clean = new URL((displayUrl || url).toString());
          clean.searchParams.delete('section_id');
          history.replaceState({}, '', clean.toString());
        })
        .catch(function (e) {
          root.removeAttribute('data-loading');
          if (e.name === 'AbortError') return;
          /* Fall back to a real navigation rather than leaving the user stuck. */
          window.location.assign(url.toString());
        });
    }

    /* ---- wiring ------------------------------------------------------- */
    /* Whatever the interaction, the address bar keeps the catalogue path and
       the active term; only the fetch goes to /search. */
    function displayFor(url) {
      var si = root.querySelector('[data-fok-shop-search] input[type="search"]');
      var term = si && si.value.trim() !== '' ? si.value.trim() : '';
      if (!term) return undefined;
      var shown = new URL(window.location.pathname, window.location.origin);
      url.searchParams.forEach(function (v, k) {
        if (k !== 'q' && k !== 'type' && k !== 'section_id') shown.searchParams.append(k, v);
      });
      shown.searchParams.set('q', term);
      return shown;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var u = buildUrl();
      render(u, displayFor(u));
    });

    var priceTimer = null;
    function bindSidebar() {
      /* Re-bound after every swap, since the sidebar's DOM is replaced. */
      Array.prototype.forEach.call(form.querySelectorAll('[data-fok-shop-more]'), function (btn) {
        if (btn.__bound) return;
        btn.__bound = true;
        btn.addEventListener('click', function () {
          var group = btn.closest('[data-fok-shop-group]');
          if (!group) return;
          var extras = group.querySelectorAll('[data-extra]');
          var opening = extras.length > 0 && extras[0].hidden;
          Array.prototype.forEach.call(extras, function (el) { el.hidden = !opening; });
          btn.textContent = opening ? btn.getAttribute('data-less') : btn.getAttribute('data-more');
          /* The list is capped at 236px and scrolls, so revealing options
             alone would push them below its fold and the click would look
             like it did nothing. Uncap while expanded. */
          var list = group.querySelector('[data-fok-shop-list]');
          if (list) {
            if (opening) { list.setAttribute('data-expanded', ''); }
            else { list.removeAttribute('data-expanded'); }
          }
        });
      });
    }

    form.addEventListener('change', function (e) {
      if (e.target.type === 'number') return; /* handled on input, debounced */
      var u2 = buildUrl();
      render(u2, displayFor(u2));
    });

    /* Price is typed, so it needs a pause before firing. */
    form.addEventListener('input', function (e) {
      if (e.target.type !== 'number') return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () { var u3 = buildUrl(); render(u3, displayFor(u3)); }, 500);
    });

    /* A reset link points at the bare collection URL. When the page is being
       previewed with ?view=<template>, that parameter has to survive the
       reset or the browser lands on the default collection template. Liquid
       cannot read it (Shopify exposes no request.params.view), so it is
       carried across here from the live URL. */
    /* ---- inline search --------------------------------------------------
       Typing narrows the grid in place: the catalogue section is fetched
       from /search with section_id and swapped in, so the shopper never
       leaves the page and Shopify still does the matching, the filtering and
       the pagination. The section renders search.results when the search
       object is populated, so the same markup serves both contexts.

       With JS off the form is a plain GET to /search and still works. */
    var searchForm = root.querySelector('[data-fok-shop-search]');
    if (searchForm) {
      /* Captured once: window.location.pathname moves to /search only in the
         fetch URL, never in the address bar, so the catalogue path has to be
         remembered from the page the shopper actually loaded. */
      var cataloguePath = window.location.pathname;
      var searchTimer = null;
      var searchInput = searchForm.querySelector('input[type="search"]');

      var runSearch = function () {
        var term = searchInput ? searchInput.value.trim() : '';
        var url;
        if (term === '') {
          /* An empty box returns to the plain catalogue. */
          url = new URL(window.location.pathname, window.location.origin);
        } else {
          url = new URL('/search', window.location.origin);
          url.searchParams.set('q', term);
          url.searchParams.set('type', 'product');
        }
        var view = new URLSearchParams(window.location.search).get('view');
        if (view) url.searchParams.set('view', view);

        /* Keep the shopper on the catalogue path; carry the term so the URL
           is shareable and the back button restores the same view. */
        var shown = new URL(cataloguePath, window.location.origin);
        if (term !== '') shown.searchParams.set('q', term);
        if (view) shown.searchParams.set('view', view);

        render(url, shown);
      };

      searchForm.addEventListener('submit', function (e) {
        e.preventDefault();
        clearTimeout(searchTimer);
        runSearch();
      });
      searchForm.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(runSearch, 400);
      });
    }

    function resetUrl(href) {
      var url = new URL(href, window.location.origin);
      var view = new URLSearchParams(window.location.search).get('view');
      if (view) url.searchParams.set('view', view);
      return url;
    }

    /* The sidebar's DOM is replaced on every filter, so a reference captured
       once would point at a detached node. Delegate from `root`, which
       survives. */
    root.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-fok-shop-clear]') : null;
      if (!link) return;
      e.preventDefault();
      render(resetUrl(link.getAttribute('href')));
    });

    ['[data-fok-shop-sort]', '[data-fok-shop-perpage]'].forEach(function (sel) {
      var el = root.querySelector(sel);
      if (el) el.addEventListener('change', function () { var u4 = buildUrl(); render(u4, displayFor(u4)); });
    });

    /* The design's empty state offers the same reset as the sidebar. */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('.fok-shopempty__btn') : null;
      if (!btn) return;
      e.preventDefault();
      render(resetUrl(btn.getAttribute('href')));
    });

    bindSidebar();
  }

  /* ---- recently viewed ------------------------------------------------ */
  var RECENT_KEY = 'fok:recent';

  function recordCurrentProduct() {
    var el = document.querySelector('[data-fok-product-handle]');
    if (!el) return;
    var handle = el.getAttribute('data-fok-product-handle');
    if (!handle) return;
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      list = list.filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 10)));
    } catch (e) { /* private mode: skip silently */ }
  }

  function renderRecent() {
    var wrap = document.querySelector('[data-fok-shop-recent]');
    var grid = document.querySelector('[data-fok-shop-recent-grid]');
    if (!wrap || !grid) return;
    var handles;
    try {
      handles = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) { return; }
    if (!handles.length) return;

    /* Each card is fetched as its own product page section-render. Kept to
       five, matching the design's five-column recent grid. */
    var wanted = handles.slice(0, 5);
    Promise.all(wanted.map(function (h) {
      return fetch('/products/' + encodeURIComponent(h) + '?view=fok-card')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    })).then(function (parts) {
      var html = parts.filter(Boolean).join('');
      if (!html.trim()) return;
      grid.innerHTML = html;
      wrap.hidden = false;
    });
  }

  /* The mobile filter accordion.

     The wrapper is display:contents above 767px so the sidebar lays out as
     though the <details> were not there — but a CLOSED <details> keeps its
     non-summary children out of normal flow regardless of display, so the
     attribute state, not the CSS, is what decides whether the filters are
     visible. Left alone, a drawer the user closed on a phone stays closed
     after a resize to tablet or desktop, where the filters must always be
     shown and there is no visible toggle to reopen them.

     So the attribute is synced to the breakpoint: forced open at >=768px,
     and closed on the way down only on the crossing, so a user who opened
     it on a phone keeps it open. */
  function initFilterBreakpoint() {
    var MOBILE = '(max-width: 767px)';
    var mq = window.matchMedia(MOBILE);

    function sync() {
      var isMobile = mq.matches;
      Array.prototype.forEach.call(
        document.querySelectorAll('[data-fok-shop-filts]'),
        function (d) {
          if (isMobile) {
            if (d.getAttribute('data-fok-mobile') !== '1') {
              d.setAttribute('data-fok-mobile', '1');
              d.removeAttribute('open');
            }
          } else {
            d.removeAttribute('data-fok-mobile');
            d.setAttribute('open', '');
          }
        }
      );
    }

    sync();
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', sync);
    else mq.addListener(sync); /* Safari < 14 */
    return sync;
  }

  var syncFilterBreakpoint = null;

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-fok-shop]'), init);
    if (!syncFilterBreakpoint) syncFilterBreakpoint = initFilterBreakpoint();
    else syncFilterBreakpoint();
    recordCurrentProduct();
    renderRecent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
